/**
 * Convierte minutos a formato HH:mm (valor absoluto).
 */
function formatHoursNumber(hours) {
	return Math.abs(Math.floor(hours))
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function formatHHmmHours(hours) {
	return hours >= 100
		? formatHoursNumber(hours)
		: String(hours).padStart(2, "0");
}

export function minutesToHHmm(minutes) {
	const abs = Math.abs(Math.round(minutes));
	const h = Math.floor(abs / 60);
	const m = abs % 60;
	return `${formatHHmmHours(h)}:${String(m).padStart(2, "0")}`;
}

export function minutesToSignedHHmm(minutes) {
	const sign = minutes < 0 ? "-" : minutes > 0 ? "+" : "";
	return `${sign}${minutesToHHmm(minutes)}`;
}

/** Formatea horas y minutos de duración a HH:mm. */
export function formatDurationHHmm(hours, minutes) {
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Convierte partes de duración a minutos totales. */
export function durationPartsToMinutes(hours, minutes) {
	return Math.max(0, hours || 0) * 60 + Math.max(0, minutes || 0);
}

/** Convierte minutos totales a partes { hours, minutes }. */
export function minutesToDurationParts(minutes) {
	const abs = Math.abs(Math.round(minutes || 0));
	return {
		hours: Math.floor(abs / 60),
		minutes: abs % 60,
	};
}

export function isValidDurationParts(hours, minutes) {
	const mins = minutes ?? 0;
	if (mins < 0 || mins > 59) return false;
	return durationPartsToMinutes(hours, mins) > 0;
}

/** Máximo de dígitos: horas (5) + minutos (2). */
export const DURATION_DIGIT_LIMIT = 7;

/** Muestra horas (mín. 2 dígitos si <100) y minutos siempre a 2 dígitos. */
export function formatDurationDisplay(hours, minutes) {
	return `${formatHHmmHours(hours)}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Formato visual mientras se escribe. Con 4+ dígitos, los 2 últimos son minutos.
 * Con 3 dígitos y minutos > 59, se reinterpretan como HH:M (p. ej. 190 → 19:0).
 * Con 2 dígitos <=59 se interpretan como minutos; si no, horas parciales (p. ej. 60:).
 */
export function formatDurationDigitsForDisplay(digits) {
	const d = String(digits || "")
		.replace(/\D/g, "")
		.slice(0, DURATION_DIGIT_LIMIT);
	if (!d) return "";

	const len = d.length;

	if (len === 1) {
		return formatDurationHHmm(0, parseInt(d, 10));
	}

	if (len === 2) {
		const val = parseInt(d, 10);
		if (val <= 59) return formatDurationHHmm(0, val);
		return `${d}:`;
	}

	if (len === 3) {
		const minutesFromLastTwo = parseInt(d.slice(-2), 10);
		if (minutesFromLastTwo > 59) {
			const hours = parseInt(d.slice(0, -1), 10);
			const minuteTens = parseInt(d.slice(-1), 10);
			return `${formatHHmmHours(hours)}:${minuteTens}`;
		}
	}

	const minutes = parseInt(d.slice(-2), 10);
	if (minutes > 59) return null;
	const hours = parseInt(d.slice(0, -2) || "0", 10);
	return formatDurationDisplay(hours, minutes);
}

/** Indica si se puede añadir un dígito sin superar 59 minutos. */
export function canAppendDurationDigit(currentDigits, digit) {
	const next = `${currentDigits}${digit}`.slice(0, DURATION_DIGIT_LIMIT);
	if (next.length <= currentDigits.length) return false;
	if (next.length >= 4) {
		const minutes = parseInt(next.slice(-2), 10);
		if (minutes > 59) return false;
	}
	return formatDurationDigitsForDisplay(next) !== null;
}

export function durationDigitsToMinutes(digits) {
	const d = String(digits || "")
		.replace(/\D/g, "")
		.slice(0, DURATION_DIGIT_LIMIT);
	if (!d) return 0;

	const len = d.length;

	if (len === 1) return parseInt(d, 10);

	if (len === 2) {
		const val = parseInt(d, 10);
		if (val <= 59) return val;
		const hours = parseInt(d.slice(0, -1), 10);
		const minutes = parseInt(d.slice(-1), 10);
		return hours * 60 + minutes;
	}

	if (len === 3) {
		const minutesFromLastTwo = parseInt(d.slice(-2), 10);
		if (minutesFromLastTwo > 59) {
			const hours = parseInt(d.slice(0, -1), 10);
			const minuteTens = parseInt(d.slice(-1), 10);
			return hours * 60 + minuteTens * 10;
		}
	}

	const minutes = parseInt(d.slice(-2), 10);
	if (minutes > 59) return 0;
	const hours = parseInt(d.slice(0, -2) || "0", 10);
	return hours * 60 + minutes;
}

export function isValidDurationMinutes(minutes) {
	return (minutes || 0) > 0;
}

/** Convierte minutos a dígitos: horas + minutos (2 dígitos). */
export function minutesToDurationDigits(minutes) {
	const { hours, minutes: mins } = minutesToDurationParts(minutes);
	if (hours === 0 && mins === 0) return "";
	return `${hours}${String(mins).padStart(2, "0")}`;
}

/** Acepta pegado en formato H+:mm o solo dígitos. */
export function parseDurationPaste(text) {
	const trimmed = String(text || "").trim();
	if (!trimmed) return "";

	const hhmmMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
	if (hhmmMatch) {
		const hours = parseInt(hhmmMatch[1], 10);
		const minutes = parseInt(hhmmMatch[2], 10);
		if (minutes <= 59) {
			return minutesToDurationDigits(hours * 60 + minutes);
		}
		return null;
	}

	const digits = trimmed.replace(/\D/g, "").slice(0, DURATION_DIGIT_LIMIT);
	if (!digits) return null;
	return formatDurationDigitsForDisplay(digits) === null ? null : digits;
}

/**
 * Convierte minutos a texto de visualización para filas numéricas (ej. 2h 30m).
 */
export function minutesToDisplay(minutes) {
	if (minutes === 0) return "0";
	const sign = minutes < 0 ? "-" : "";
	const abs = Math.abs(minutes);
	const h = Math.floor(abs / 60);
	const m = Math.round(abs % 60);
	const formattedHours = formatHoursNumber(h);
	if (m === 0) return `${sign}${formattedHours}h`;
	return `${sign}${formattedHours}h ${String(m).padStart(2, "0")}m`;
}

export const ACCUMULATED_ORIGIN = {
	CONTRACT: "CONTRACT",
	FIRST_CLOCK_IN: "FIRST_CLOCK_IN",
};

export const ACCUMULATED_VIEW = {
	BOTH: "both",
	CONTRACT: "contract",
	FIRST_CLOCK_IN: "firstClockIn",
};

export function parseAccumulatedView(value) {
	if (
		value === ACCUMULATED_VIEW.CONTRACT ||
		value === ACCUMULATED_VIEW.FIRST_CLOCK_IN ||
		value === ACCUMULATED_VIEW.BOTH
	) {
		return value;
	}
	return ACCUMULATED_VIEW.BOTH;
}

export function defaultAccumulatedView(hasClockIns) {
	return hasClockIns
		? ACCUMULATED_VIEW.FIRST_CLOCK_IN
		: ACCUMULATED_VIEW.CONTRACT;
}

export function pickCumulativeAfterManual(weekStats = {}, viewMode) {
	if (viewMode === ACCUMULATED_VIEW.CONTRACT) {
		return weekStats.cumulativeAfterManualContract ?? 0;
	}
	if (viewMode === ACCUMULATED_VIEW.FIRST_CLOCK_IN) {
		return weekStats.cumulativeAfterManualFirstClockIn ?? 0;
	}
	return weekStats.cumulativeAfterManualMinutes ?? 0;
}

const BALANCE_ROW_REGULAR = new Set([
	"balance",
	"acumulado",
	"acumuladoContract",
	"acumuladoFirstClockIn",
]);
const BALANCE_ROW_BOLD = new Set(["balanceAfterManual", "acumuladoAfterManual"]);

export function getFirstClockInDay(reglaTransactions = []) {
	let min = null;
	reglaTransactions.forEach((tx) => {
		const day = tx?.day;
		if (!day) return;
		if (min === null || day < min) min = day;
	});
	return min;
}

export function filterTransactionsFromDay(transactions = [], fromDay) {
	if (!fromDay) return [];
	return transactions.filter((tx) => tx?.day && tx.day >= fromDay);
}

export function hasExistingBalanceAdjustments(manualTransactions = []) {
	return manualTransactions.length > 0;
}

export function resolveAccumulatedOrigin(hasExistingBalance) {
	return hasExistingBalance
		? ACCUMULATED_ORIGIN.CONTRACT
		: ACCUMULATED_ORIGIN.FIRST_CLOCK_IN;
}

export function sumTransactionMinutes(transactions = []) {
	return transactions.reduce(
		(sum, tx) => sum + transactionValueToMinutes(tx.value),
		0
	);
}

function transactionsInWeek(transactions, weekStart, weekEnd) {
	return transactions.filter(
		(tx) => tx?.day && tx.day >= weekStart && tx.day <= weekEnd
	);
}

function weekMinutesFromDay(transactions, weekStart, weekEnd, fromDay) {
	if (!fromDay || fromDay > weekEnd) return 0;
	const start = fromDay > weekStart ? fromDay : weekStart;
	return sumTransactionMinutes(
		transactions.filter((tx) => tx?.day && tx.day >= start && tx.day <= weekEnd)
	);
}

/**
 * Las horas fichadas llegan como un total por semana y otro histórico. Se colocan
 * al final de cada rango: al ser el primer fichaje el día más antiguo con horas,
 * ese total coincide con la suma desde ese día.
 */
export function clockedHoursAsReglaTransactions({
	weeks = [],
	hoursByWeek = [],
	historyEnd,
	historyHours = 0,
} = {}) {
	const reglaTxs = weeks
		.map((week, index) => ({
			day: week.end,
			value: hoursByWeek[index] || 0,
		}))
		.filter((tx) => tx.value);
	const histReglaTxs =
		historyHours && historyEnd ? [{ day: historyEnd, value: historyHours }] : [];
	return { reglaTxs, histReglaTxs };
}

/** Primer día con horas dentro de [start, end], en fechas ISO. */
export async function findFirstDayWithHours(start, end, hoursBetween) {
	let low = start;
	let high = end;
	if (!low || !high || high < low) return null;
	const total = await hoursBetween(low, high);
	if (!(total > 0)) return null;

	while (low < high) {
		const mid = addIsoDays(low, Math.floor(isoDayDiff(low, high) / 2));
		const hours = await hoursBetween(low, mid);
		if (hours > 0) high = mid;
		else low = addIsoDays(mid, 1);
	}
	return low;
}

function addIsoDays(iso, days) {
	const date = new Date(`${iso}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + days);
	return date.toISOString().slice(0, 10);
}

function isoDayDiff(start, end) {
	const msPerDay = 24 * 60 * 60 * 1000;
	return Math.round(
		(Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) /
			msPerDay
	);
}

/**
 * Calcula el balance semanal y los dos acumulados:
 * - según contrato: todas las transacciones históricas
 * - desde primer fichaje: ignora objetivo/fichajes/manuales anteriores al primer fichaje
 *
 * Si el empleado ya tiene apuntes manuales, el acumulado canónico (después de
 * apuntes) se mantiene según contrato para no alterar balances ya ajustados.
 */
export function buildWeeklyBalanceStats({
	weeks = [],
	reglaTxs = [],
	objetivoTxs = [],
	manualTxs = [],
	histReglaTxs = [],
	histObjetivoTxs = [],
	histManualTxs = [],
	firstClockInDay: firstClockInDayOverride,
} = {}) {
	const firstClockInDay =
		firstClockInDayOverride === undefined
			? getFirstClockInDay([...histReglaTxs, ...reglaTxs])
			: firstClockInDayOverride;
	const hasExistingBalance = hasExistingBalanceAdjustments([
		...histManualTxs,
		...manualTxs,
	]);
	const canonicalOrigin = resolveAccumulatedOrigin(hasExistingBalance);

	const contractHist =
		sumTransactionMinutes(histReglaTxs) -
		sumTransactionMinutes(histObjetivoTxs) +
		sumTransactionMinutes(histManualTxs);

	const firstClockHist = firstClockInDay
		? sumTransactionMinutes(
				filterTransactionsFromDay(histReglaTxs, firstClockInDay)
		  ) -
		  sumTransactionMinutes(
				filterTransactionsFromDay(histObjetivoTxs, firstClockInDay)
		  ) +
		  sumTransactionMinutes(
				filterTransactionsFromDay(histManualTxs, firstClockInDay)
		  )
		: 0;

	let prevContractAfterManual = contractHist;
	let prevFirstClockAfterManual = firstClockHist;

	const weekStats = weeks.map((week) => {
		const weekStart = week.start;
		const weekEnd = week.end;
		const weekRegla = transactionsInWeek(reglaTxs, weekStart, weekEnd);
		const weekObjetivo = transactionsInWeek(objetivoTxs, weekStart, weekEnd);
		const weekManual = transactionsInWeek(manualTxs, weekStart, weekEnd);

		const workedMinutes = sumTransactionMinutes(weekRegla);
		const objetivoMinutes = sumTransactionMinutes(weekObjetivo);
		const manualMinutes = sumTransactionMinutes(weekManual);
		const balanceMinutes = workedMinutes - objetivoMinutes;
		const balanceAfterManualMinutes = balanceMinutes + manualMinutes;

		const firstClockBalance =
			weekMinutesFromDay(weekRegla, weekStart, weekEnd, firstClockInDay) -
			weekMinutesFromDay(weekObjetivo, weekStart, weekEnd, firstClockInDay);
		const firstClockManual = weekMinutesFromDay(
			weekManual,
			weekStart,
			weekEnd,
			firstClockInDay
		);

		const cumulativeMinutesContract = prevContractAfterManual + balanceMinutes;
		const cumulativeAfterManualContract =
			cumulativeMinutesContract + manualMinutes;
		const cumulativeMinutesFirstClockIn =
			prevFirstClockAfterManual + firstClockBalance;
		const cumulativeAfterManualFirstClockIn =
			cumulativeMinutesFirstClockIn + firstClockManual;

		prevContractAfterManual = cumulativeAfterManualContract;
		prevFirstClockAfterManual = cumulativeAfterManualFirstClockIn;

		const useContract = canonicalOrigin === ACCUMULATED_ORIGIN.CONTRACT;

		return {
			workedMinutes,
			objetivoMinutes,
			manualMinutes,
			weekManualTxs: weekManual,
			balanceMinutes,
			balanceAfterManualMinutes,
			cumulativeMinutesContract,
			cumulativeMinutesFirstClockIn,
			cumulativeAfterManualContract,
			cumulativeAfterManualFirstClockIn,
			cumulativeMinutes: useContract
				? cumulativeMinutesContract
				: cumulativeMinutesFirstClockIn,
			cumulativeAfterManualMinutes: useContract
				? cumulativeAfterManualContract
				: cumulativeAfterManualFirstClockIn,
		};
	});

	return {
		firstClockInDay,
		canonicalOrigin,
		hasExistingBalance,
		weeks: weekStats,
	};
}

export function balanceSpanClass(rowId) {
	if (BALANCE_ROW_BOLD.has(rowId)) {
		return "tb-balance-span tb-balance-span--bold";
	}
	if (BALANCE_ROW_REGULAR.has(rowId)) {
		return "tb-balance-span tb-balance-span--regular";
	}
	return "tb-balance-span";
}

/** Convierte horas decimales de transacción a minutos. */
export function transactionValueToMinutes(value) {
	return Math.round((value || 0) * 60);
}

/** Convierte minutos industriales (entero positivo) a horas decimales para la API. */
export function industrialMinutesToHours(minutes, operation) {
	const absMinutes = Math.abs(parseInt(minutes, 10) || 0);
	const hours = absMinutes / 60;
	return operation === "subtract" ? -hours : hours;
}

/** Agrupa transacciones manuales por fecha (YYYY-MM-DD). */
export function groupManualEntriesByDay(entries) {
	const groups = {};
	entries.forEach((entry) => {
		const day = entry.day;
		if (!groups[day]) groups[day] = [];
		groups[day].push(entry);
	});
	return Object.keys(groups)
		.sort()
		.map((day) => ({ day, entries: groups[day] }));
}
